import React from "react";
import moment from "moment";

import {WithDeleteDialogZzFlatButton, ZzFlatButton} from "core/components/buttons/buttons";
import PageAncestor from "core/common/page/pageAncestor";
import Toolmenu from "core/components/toolmenu/toolmenu";
import mdEntityService from "core/metamodel/mdEntityService";
import {ErrorComp} from "core/components/errorComp/errorComp";
import {IDomainObject, lovValue, preSave} from "core/form/formUtils";
import ConfirmationDialog from "core/components/confirmationDialog/confirmationDialog";
import {MODULE, PageProps, PERMISSION, SECURABLE} from "core/metamodel/metaConstant";
import "invoice/page/invoiceDetail.less";
import {observer} from "mobx-react";
import {action, ObservableMap} from "mobx";
import uiService from "core/common/service/uiService";
import MdEntity from "core/metamodel/mdEntity";
import {Shipment, SHIPMENTDIRECTION, SHIPMENTSTATUS, ShipmentVisit} from "shipment/shipmentDomain";
import shipmentService from "shipment/shipmentService";
import ShipmentOutgoingForm from "shipment/component/shipmentOutgoingForm";
import {loc} from "core/common/config/locales";
import ShipmentIncomingForm from "shipment/component/shipmentIncomingForm";
import {MovingBlocks} from "core/grid/gridUtils";
import {GridDocument} from "core/document/gridDocument";
import GridDocumentComp from "core/document/component/gridDocumentComp";


const shipmentGridDocument = 'shipmentGridDocument';

@observer
export default class ShipmentDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.shipment;
  static title = loc.i18n`Shipment`;
  static icon = 'fighter-jet';
  static protectedPage = true;


  static fetchData(routerParams, query) {
    console.log("ShipmentDetail#fetchData(%O)", query);

    let metadataPromise = mdEntityService.fetchEntityMetadata(['Shipment', 'ShipmentVisit'], [{entity: 'SHIPMENTSTATUS', lov: true}, 'Document']);

    let shipmentPromise: Promise<Shipment>;

    if (routerParams.id === 'new') {
      let shippedTo = (query.partyId) ? {value: query.partyId, label: query.partyLabel} : null;
      let shipmentDirection = query.shipmentDirection;
      let today = moment().startOf('day');
      shipmentPromise = Promise.resolve(new Shipment({
        $new: true,
        shipmentDate: today,
        shippedTo,
        shipmentDirection,
        visitOrder: 1
      }));
    } else if (routerParams.id === 'copy') {
      shipmentPromise = shipmentService.readShipment(query.copyId, {mode: 'copy'})
        .then( (shipmentObject: Shipment) => {
          shipmentObject.cleanOnCopy();
          shipmentObject.shipmentId = undefined;
          shipmentObject.shipmentVisit = undefined;
          shipmentObject.visitOrder = 1;

          return shipmentService.computeField("disponentParty", {
            shippedToAddress: lovValue(shipmentObject.shippedToAddress)
          })
            .then(action("computeDisponent", (v: any) => {
              shipmentObject.disponentParty = v;
              return shipmentObject;
            }));
        });
    } else {
      shipmentPromise = shipmentService.readShipment(routerParams.id);
    }


    return Promise.all([metadataPromise, shipmentPromise])
      .then(([entityMap, shipmentObject]: [ObservableMap<MdEntity>, Shipment]) => {
        shipmentObject.$new = !shipmentObject.shipmentId;

        if (!shipmentObject.shipmentStatus) shipmentObject.shipmentStatus = entityMap.get("SHIPMENTSTATUS").getLovItem(SHIPMENTSTATUS.NOTREADY);

        shipmentObject.$movingBlocks = new MovingBlocks(null, false, null,
          new GridDocument(shipmentGridDocument, "Docs", {shipmentId: shipmentObject.shipmentId}, false, null, (shipmentObject.$new)?[]:null)
        );

        shipmentService.shipmentObject = shipmentObject.initAfterRead(routerParams.id === 'new');
        return (shipmentObject.shipmentVisit) ? shipmentService.readShipmentVisit(lovValue(shipmentObject.shipmentVisit)) : Promise.resolve(null);
      }).then((shipmentVisit: ShipmentVisit) => {
        shipmentService.shipmentObject.$shipmentVisit = shipmentVisit;
      });
  }


  componentDidUpdate(prevProps: PageProps) {
    if (this.idChanged(prevProps.params.id, this.props.params.id)) {
      shipmentService.shipmentObject.$movingBlocks.searchExposedGrids(shipmentService.shipmentObject);
    }
  }


  componentDidMount() {
    console.debug("%c componentDidMount", "background-color: magenta");
    shipmentService.shipmentObject.$movingBlocks.searchExposedGrids(shipmentService.shipmentObject);
  }

  componentWillUnmount() {
    shipmentService.shipmentObject = null;
  }



  customValidate = (shipmentObject: Shipment) => {
    let errors = [];

    return errors;
  };


  findRootObject() {
    return shipmentService.shipmentObject;
  }


  @action onSave = (evt) => {
    console.log('onSave shipmentId = %s, $new = %s', shipmentService.shipmentObject.shipmentId, shipmentService.shipmentObject.$new);
    uiService.showToolbarActionFlag();

    let result = preSave(shipmentService.shipmentObject, this.customValidate, true);
    if (!result) {
      return;
    }

    if (shipmentService.shipmentObject.$shipmentVisit && !shipmentService.shipmentObject.$shipmentVisit.shipmentVisitId) {
      shipmentService.shipmentObject.setTuple("shipmentVisit", shipmentService.shipmentObject.$shipmentVisit);
    }

    let wasNew: boolean;

    shipmentService.saveShipment(shipmentService.shipmentObject)
      .then(action("saveShipment-action", (shipmentJson: IDomainObject) => {

        wasNew = shipmentService.shipmentObject.cleanAfterSave(shipmentJson, true, "$shipmentVisit");
        return this.docSavingPromise(shipmentService.shipmentObject.$movingBlocks.getBlock(shipmentGridDocument) as GridDocument, {shipmentId: shipmentService.shipmentObject.shipmentId});

      })).then(x => {
        if (wasNew) {
          this.context.router.replaceWith('shipmentDetail', {id: shipmentService.shipmentObject.shipmentId});
        }
        uiService.hideToolbarActionFlag();
      }).catch(error => {
        console.log("Error %O", error);
        shipmentService.shipmentObject.$errors = error.getMessages();
      })

  };





  onAdd = (evt) => {
    console.log('onAdd');
    this.context.router.transitionTo("shipmentDetail", {id: 'new'}, {shipmentDirection: shipmentService.shipmentObject.shipmentDirection});
  };

  onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("shipmentDetail", {id: 'copy'}, {copyId: shipmentService.shipmentObject.shipmentId});
  };


  @action onDelete = (evt) => {
    console.log('onDelete');

    shipmentService.deleteShipment(shipmentService.shipmentObject.shipmentId)
      .then(response => {
        this.goBackOrHome();
      }, error => {
        shipmentService.shipmentObject.$errors = error.getMessages();
      });
  };






  render() {

    const {
      query,
    } = this.props;

    let shipmentObject = shipmentService.shipmentObject;
    let shipmentEntity = mdEntityService.getEntity('Shipment');

    const propsForCreateForm = {
      dataObject: shipmentObject,
      rootObject: shipmentObject,
      entity: shipmentEntity,
      entities: mdEntityService.entityMap
    };

    console.log('shipmentDetail render shipmentId = %s, $new = %s', shipmentObject.shipmentId, shipmentObject.$new);

    let movingBlocks = shipmentService.shipmentObject.$movingBlocks;

    return (

      <main className="main-content" style={{display: 'flex', flexDirection: 'column'}}>
        {this._createToolMenu(shipmentObject, query.gridLocation)}
        <ErrorComp messages={shipmentObject.$errors}/>
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>

        {(shipmentObject.shipmentDirection === SHIPMENTDIRECTION.OUTGOING) ? <ShipmentOutgoingForm {...propsForCreateForm} /> : <ShipmentIncomingForm {...propsForCreateForm} /> }

        <GridDocumentComp uiLocation={movingBlocks.getUiLocation(shipmentGridDocument)} grid={movingBlocks.getBlock(shipmentGridDocument) as GridDocument}
                          entities={mdEntityService.entityMap} rootObject={shipmentService.shipmentObject}/>

      </main>
    );
  }


  _createToolMenu(shipmentObject: Shipment, gridLocation: string) {
    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1} historize
                entityName="Shipment" entityId={shipmentObject.shipmentId}>
        <ZzFlatButton onClick={this.onSave} disabled={!shipmentObject.$dirty} label={loc.i18n`Save`} permission={shipmentObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} disabled={shipmentObject.$new} label={loc.i18n`New`} permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={shipmentObject.$new} label={loc.i18n`Copy`} permission={PERMISSION.New}/>
        <WithDeleteDialogZzFlatButton entityName="Shipment" pageAncestor={this} onClick={this.onDelete} disabled={shipmentObject.$new} label={loc.i18n`Delete`} permission={PERMISSION.Delete}/>
        <ZzFlatButton onClick={this.createShipmentVisit} disabled={shipmentObject.$new || shipmentObject.shipmentVisit} label={loc.i18n`Create shipment visit`} securable={SECURABLE.shipmentVisitDetail} permission={PERMISSION.New}/>



      </Toolmenu>
    );
  }

  @action createShipmentVisit = (evt) => {
    console.log('createShipmentVisit');
    let params = {
      shipmentId: shipmentService.shipmentObject.shipmentId,
    }
    this.context.router.transitionTo("shipmentVisitDetail", {id: 'new'}, params);
  };


}




// WEBPACK FOOTER //
// ./src/shipment/page/shipmentDetail.tsx